import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { initAnalytics } from '@/lib/analytics';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Providers } from "@/components/providers";
import { AuthGuard } from "@/components/auth/auth-guard";
import { PublicOnlyGuard } from "@/components/auth/public-only-guard";
import { AndroidBackButtonHandler } from "@/components/android-back-button-handler";
import { useStatusBar } from "@/hooks/use-status-bar";
import { useTheme } from "@/contexts/theme-context";
import { NetworkStatus } from "@/components/network-status";
import HomePage from "@/components/pages/home-page";
import LoginPage from "@/components/pages/login-page";
import SignupPage from "@/components/pages/signup-page";
import SettingsPage from "@/components/pages/settings-page";
import SubscriptionTestPage from "@/components/pages/subscription-test-page";
import PasswordResetPage from "@/components/pages/password-reset-page";
import PasswordResetConfirmPage from "@/components/pages/password-reset-confirm-page";
import AuthActionPage from "@/components/pages/auth-action-page";
import PrivacyPolicyPage from "@/components/pages/privacy-policy-page";
import TermsConditionsPage from "@/components/pages/terms-conditions-page";

function DeepLinkHandler() {
  const navigate = useNavigate();
  const handledInitialLink = useRef(false);

  useEffect(() => {
    // Only handle deep links on native platforms
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Handle app URL open event (deep link)
    const handleAppUrl = async (event: { url: string }) => {
      try {
        const url = new URL(event.url);
        const path = url.pathname + url.search;

        // Navigate to the path from the deep link
        navigate(path, { replace: true });
      } catch (error) {
        console.error('Error handling deep link:', error);
      }
    };

    // Check if app was opened with a URL
    CapacitorApp.getLaunchUrl().then((result) => {
      if (result?.url && !handledInitialLink.current) {
        handledInitialLink.current = true;
        handleAppUrl({ url: result.url });
      }
    });

    // Listen for app URL open events (when app is already running)
    const listener = CapacitorApp.addListener('appUrlOpen', handleAppUrl);

    return () => {
      listener.then(l => l.remove());
    };
  }, [navigate]);

  return null;
}

function ThemeAwareStatusBar() {
  const { theme } = useTheme();
  useStatusBar(theme === 'dark');
  return null;
}

function AppContent() {
  return (
    <>
      <ThemeAwareStatusBar />
      <NetworkStatus />
      <Routes>
        <Route path="/" element={
          <AuthGuard>
            <HomePage />
          </AuthGuard>
        } />
        <Route path="/login" element={
          <PublicOnlyGuard>
            <LoginPage />
          </PublicOnlyGuard>
        } />
        <Route path="/signup" element={
          <PublicOnlyGuard>
            <SignupPage />
          </PublicOnlyGuard>
        } />
        <Route path="/reset-password" element={
          <PublicOnlyGuard>
            <PasswordResetPage />
          </PublicOnlyGuard>
        } />
        <Route path="/reset-password-confirm" element={
          <PublicOnlyGuard>
            <PasswordResetConfirmPage />
          </PublicOnlyGuard>
        } />
        <Route path="/__/auth/action" element={<AuthActionPage />} />
        <Route path="/legal/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/legal/terms-conditions" element={<TermsConditionsPage />} />
        <Route path="/settings" element={
          <AuthGuard>
            <SettingsPage />
          </AuthGuard>
        } />
        <Route path="/subscription-test" element={
          <AuthGuard>
            <SubscriptionTestPage />
          </AuthGuard>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  // Initialise analytics once on app startup
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <Router>
      <Providers>
        <DeepLinkHandler />
        <AndroidBackButtonHandler />
        <AppContent />
      </Providers>
    </Router>
  );
}
